import { useState } from "react";
import type { ReactElement } from "react";
import { useSearchParams } from "react-router-dom";

import { apiClient } from "../api/client";
import { useTenant } from "../contexts/TenantContext";

interface MagicLinkPayload {
  email: string;
  name?: string;
  company?: string;
  role?: string;
  tenant_slug?: string;
}

export function LoginPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get("error");
  // Cycle 22 STORY-U2 — detect tenant context (subdomain или /t/<slug>/login)
  const tenant = useTenant();

  const [form, setForm] = useState<MagicLinkPayload>({ email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(
    errorParam === "magic_link_expired"
      ? "Ссылка для входа уже использована или истекла. Запросите новую."
      : errorParam === "magic_link_invalid"
      ? "Magic link повреждён. Запросите новую ссылку."
      : null,
  );
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Cycle 22 STORY-U2: bind login attempt к tenant если URL это указывает
      const payload: MagicLinkPayload = { ...form };
      if (tenant.slug) {
        payload.tenant_slug = tenant.slug;
      }
      await apiClient.post("/auth/magic-link/request", payload);
      setSubmitted(true);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Не удалось отправить ссылку";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <section className="auth-card">
        <h1>Проверьте email</h1>
        <p>
          Мы отправили ссылку для входа на <strong>{form.email}</strong>.
          Ссылка действительна 15 минут.
        </p>
        <p className="auth-hint">
          Не видите письмо? Проверьте папку «Спам» или попробуйте запросить
          ссылку повторно.
        </p>
        <button
          className="btn-secondary"
          type="button"
          onClick={() => setSubmitted(false)}
        >
          Запросить ещё раз
        </button>
      </section>
    );
  }

  return (
    <section className="auth-card">
      {tenant.slug ? (
        <>
          <h1>Вход в кабинет</h1>
          <p className="auth-subtitle">
            Вы входите в личный кабинет клиента{" "}
            <code style={{ background: "#f0f7ff", padding: "2px 8px", borderRadius: "3px" }}>
              {tenant.slug}
            </code>
            . Получите ссылку для входа на email — действительна 15 минут.
          </p>
        </>
      ) : (
        <>
          <h1>Вход в Client Portal</h1>
          <p className="auth-subtitle">
            Получите ссылку для входа на email. Или используйте invite-ссылку
            от вашего менеджера.
          </p>
        </>
      )}

      <form onSubmit={handleSubmit} className="auth-form">
        <label>
          <span>Email *</span>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@company.com"
            autoComplete="email"
          />
        </label>
        <label>
          <span>Имя</span>
          <input
            type="text"
            value={form.name ?? ""}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Иван Иванов"
            autoComplete="name"
          />
        </label>
        <label>
          <span>Компания</span>
          <input
            type="text"
            value={form.company ?? ""}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            placeholder="ООО Ромашка"
            autoComplete="organization"
          />
        </label>
        <label>
          <span>Должность</span>
          <input
            type="text"
            value={form.role ?? ""}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            placeholder="Директор по производству"
          />
        </label>

        {error && <p className="auth-error">{error}</p>}

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? "Отправляем…" : "Отправить ссылку"}
        </button>
      </form>
    </section>
  );
}
